import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Text,
  VStack,
  Spinner,
  Center,
  useToast,
  IconButton,
  Flex,
  Container,
  Button,
} from '@chakra-ui/react';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { getAffiliate, getCampaign, createAffiliate, Affiliate, Campaign } from '../../../services/api/referrals';
import { getInfluencerPrimaryHandle } from '../../../utils/influencerUtils';
import { ArrowBackIcon, CopyIcon, CheckIcon } from '@chakra-ui/icons';
import { FiUsers, FiTrendingUp, FiTarget, FiPercent } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DisplayHeading } from '../../../components/ui';
import { ff } from '../../../theme';

const MotionBox = motion(Box);

const formatCurrency = (cents: number, currency: string = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
};

export const AffiliateDetails: React.FC = () => {
  const { influencerData } = useContext(InfluencerDataContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [affiliateData, setAffiliateData] = useState<Affiliate | null>(null);
  const [campaignData, setCampaignData] = useState<Campaign | null>(null);
  const [hasCopied, setHasCopied] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  

  useEffect(() => {
    if (influencerData?.id && influencerData.rewardfulAffiliateId) {
      loadAffiliateData();
    } else if (influencerData?.id && !influencerData.rewardfulAffiliateId) {
      // Automatically create affiliate if user doesn't have one
      handleCreateAffiliate();
    }
  }, [influencerData?.id, influencerData?.rewardfulAffiliateId]);

  const loadAffiliateData = async () => {
    if (!influencerData?.id || !influencerData.rewardfulAffiliateId) return;
    setIsLoading(true);

    try {
      const response = await getAffiliate(influencerData.rewardfulAffiliateId);
      
      if (response.success && response.data) {
        setAffiliateData(response.data);
        
        // Fetch complete campaign data if we have a campaign ID
        if (response.data.campaign?.id) {
          const campaignResponse = await getCampaign(response.data.campaign.id);
          if (campaignResponse.success && campaignResponse.data) {
            setCampaignData(campaignResponse.data);
          } else {
            console.error('Failed to load campaign data:', campaignResponse.error);
          }
        }
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to load affiliate data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load affiliate data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAffiliate = async () => {
    if (!influencerData?.id) return;
    setIsCreating(true);

    try {
      const response = await createAffiliate({
        first_name: influencerData.firstName,
        last_name: influencerData.lastName,
        email: influencerData.email_address,
        influencerHandle: getInfluencerPrimaryHandle(influencerData)
      }, influencerData.id);

      if (response.success && response.data) {
        setAffiliateData(response.data);
        
        // Fetch complete campaign data if we have a campaign ID
        if (response.data.campaign?.id) {
          const campaignResponse = await getCampaign(response.data.campaign.id);
          if (campaignResponse.success && campaignResponse.data) {
            setCampaignData(campaignResponse.data);
          }
        }
        
        toast({
          title: 'Success',
          description: 'Your affiliate account has been created!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
          } else {
          throw new Error(response.error || 'Failed to create affiliate account');
        }
    } catch (error) {
      console.error('Error creating affiliate:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create affiliate account',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setHasCopied(true);
      toast({
        title: 'Link copied!',
        status: 'success',
        duration: 2000,
      });
      setTimeout(() => setHasCopied(false), 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: 2000,
      });
    }
  };

  const getCommissionText = () => {
    if (!campaignData) return null;
    
    let commissionAmount;
    if (campaignData.reward_type === 'percent' && campaignData.commission_percent) {
      commissionAmount = `${campaignData.commission_percent}%`;
    } else if (campaignData.reward_type === 'fixed' && campaignData.commission_amount_cents) {
      commissionAmount = formatCurrency(campaignData.commission_amount_cents, campaignData.commission_amount_currency);
    } else {
      return null;
    }

    const mainText = `Earn ${commissionAmount} commission for each referral`;
    let subText = null;
    
    if (campaignData.max_commissions && campaignData.max_commission_period_months) {
      subText = `Earn up to ${campaignData.max_commissions} payments within the first ${campaignData.max_commission_period_months} months`;
    }
    
    return { mainText, subText };
  };

  const header = (
    <Flex justifyContent="space-between" alignItems="center" py={2}>
      <IconButton
        icon={<ArrowBackIcon />}
        w="44px"
        h="44px"
        aria-label="Go Back"
        variant="ghost"
        borderRadius="full"
        color={ff.ink}
        onClick={() => navigate(-1)}
        _hover={{ bg: ff.grey }}
      />
      <VStack spacing={0}>
        <DisplayHeading size="22px">Affiliate dashboard</DisplayHeading>
        <Text fontSize="13px" color={ff.text2}>
          Earn by referring brands
        </Text>
      </VStack>
      <Box width="44px" />
    </Flex>
  );

  const card = {
    border: '1px solid',
    borderColor: ff.line,
    borderRadius: '20px',
    bg: 'white',
    p: 5,
  } as const;

  if (isLoading || isCreating) {
    return (
      <Container maxW="100%" px={4} py={3}>
        <MotionBox
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <VStack spacing={3} align="stretch">
            {header}
            <Center p={8}>
              <VStack spacing={4}>
                <Spinner size="lg" thickness="3px" speed="0.65s" color={ff.ink} />
                <Text fontSize="14px" color={ff.text2}>
                  {isCreating ? 'Setting up your affiliate account...' : 'Loading affiliate data...'}
                </Text>
              </VStack>
            </Center>
          </VStack>
        </MotionBox>
      </Container>
    );
  }

  if (!affiliateData || !affiliateData.links?.[0]) {
    return (
      <Center p={4}>
        <Text fontSize="14px" color={ff.text2}>Unable to load affiliate data</Text>
      </Center>
    );
  }

  const { visitors, leads, conversions, url } = affiliateData.links[0];
  const conversionRate = visitors > 0 ? ((conversions / visitors) * 100).toFixed(1) : '0';

  const stats = [
    { icon: FiUsers, label: 'Visitors', sub: 'All time', value: visitors.toLocaleString() },
    { icon: FiTrendingUp, label: 'Leads', sub: 'All time', value: leads.toLocaleString() },
    { icon: FiTarget, label: 'Conversions', sub: 'All time', value: conversions.toLocaleString() },
    { icon: FiPercent, label: 'Conversion rate', sub: 'Average', value: `${conversionRate}%` },
  ];

  return (
    <Container maxW="100%" px={4} py={3}>
      <MotionBox
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <VStack spacing={3} align="stretch">
          {header}

          {/* Referral Link */}
          <Box {...card}>
            <Text textStyle="eyebrow" color={ff.text2} mb={3}>Your referral link</Text>
            <VStack spacing={4} align="stretch">
              {getCommissionText() && (
                <Text fontSize="14px" color={ff.body} lineHeight="1.6">
                  {getCommissionText()?.mainText}.
                  {getCommissionText()?.subText && ` ${getCommissionText()?.subText}`}
                </Text>
              )}

              <Text fontSize="14px" color={ff.body} lineHeight="1.6">
                Share this link with brands. When they sign up using your referral link, you'll earn your commission automatically.
              </Text>

              <Text fontSize="14px" fontWeight={600} color={ff.ink}>
                Your unique referral link:
              </Text>

              <Box bg={ff.grey} p={3} borderRadius="14px">
                <Text fontSize="13px" fontFamily="mono" color={ff.body} wordBreak="break-all">
                  {url}
                </Text>
              </Box>

              <Button
                variant="lime"
                leftIcon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                onClick={() => handleCopyLink(url)}
                h="48px"
                width="100%"
              >
                {hasCopied ? 'Copied!' : 'Copy link'}
              </Button>
            </VStack>
          </Box>

          {/* Performance Stats */}
          <Box {...card}>
            <Text textStyle="eyebrow" color={ff.text2} mb={1}>Performance overview</Text>
            <Text fontSize="14px" color={ff.text2} mb={2}>
              Track your affiliate success metrics
            </Text>
            <VStack spacing={0} align="stretch">
              {stats.map(({ icon: Icon, label, sub, value }, i) => (
                <Flex
                  key={label}
                  align="center"
                  justify="space-between"
                  py={3}
                  borderTop={i === 0 ? 'none' : '1px solid'}
                  borderColor={ff.line}
                >
                  <Flex align="center" gap={3}>
                    <Flex bg={ff.lime} color={ff.ink} w="36px" h="36px" borderRadius="full" align="center" justify="center">
                      <Icon size={16} />
                    </Flex>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="14px" fontWeight={600} color={ff.ink}>{label}</Text>
                      <Text fontSize="12px" color={ff.text2}>{sub}</Text>
                    </VStack>
                  </Flex>
                  <Text textStyle="display" fontSize="22px" color={ff.ink}>
                    {value}
                  </Text>
                </Flex>
              ))}
            </VStack>
          </Box>

          {/* Overview Card */}
          <Box {...card}>
            <Text textStyle="eyebrow" color={ff.text2} mb={1}>Account overview</Text>
            <Text fontSize="14px" color={ff.text2} mb={2}>
              Your affiliate account details
            </Text>
            <VStack spacing={0} align="stretch">
              <Box py={3}>
                <Text fontSize="12px" color={ff.text2} mb={1}>Full name</Text>
                <Text fontSize="14px" fontWeight={600} color={ff.ink}>{affiliateData.first_name} {affiliateData.last_name}</Text>
              </Box>
              <Box py={3} borderTop="1px solid" borderColor={ff.line}>
                <Text fontSize="12px" color={ff.text2} mb={1}>Email address</Text>
                <Text fontSize="14px" color={ff.ink} wordBreak="break-all">{affiliateData.email}</Text>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </MotionBox>
    </Container>
  );
};
